import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { discussionsReadRoute } from "app/router";
import anonymousAvatarSvg from "assets/avatars/anonymous.svg";
import compassTeamAvatarSvg from "assets/avatars/compass-team.svg";
import deletedAvatarSvg from "assets/avatars/deleted.svg";
import { Avatar, FileAttachment, ProseDocument, Spinner, Tooltip } from "components/elements";
import { CopyShareButton } from "components/elements/CopyShareButton";
import { ThreadCommentForm } from "components/form/ThreadCommentForm";
import { DiscussionComments } from "components/layout/discussion/DiscussionComments";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useUserTimezone } from "hooks/useUserTimezone";
import { formatFullTimestamp, formatRelativeTime } from "lib/relativeTime";
import { trpc } from "lib/trpc";
import { ClientDocument } from "@compass/shared/types";

export const DiscussionMessage = () => {
    const timeZone = useUserTimezone();
    const { discussionId, messageId } = useParams({ from: discussionsReadRoute.id });
    const navigate = useNavigate();
    const utils = trpc.useUtils();
    const rawQueryClient = useQueryClient();

    const numericDiscussionId = Number(discussionId);
    const numericMessageId = Number(messageId);

    const {
        data: comment,
        isLoading,
        isError,
    } = trpc.threads.getComment.useQuery(
        { commentId: numericMessageId },
        {
            enabled: !!messageId,
            retry: false,
        },
    );

    const { data: discussionView } = trpc.discussions.getDiscussionView.useQuery(
        { discussionId: numericDiscussionId },
        { enabled: Number.isFinite(numericDiscussionId) && numericDiscussionId > 0 },
    );
    const discussion = discussionView?.discussion;
    const thread = discussionView?.thread;
    const threadComments = discussionView?.comments ?? [];

    const currentUser = useCurrentUser();
    const optimisticIdRef = useRef(-1);
    const replyMutation = trpc.threads.createComment.useMutation({
        onMutate: async ({ threadId, contentJson }) => {
            if (!currentUser) return { tempId: null as number | null };
            await utils.discussions.getDiscussionView.cancel({
                discussionId: numericDiscussionId,
            });
            const tempId = optimisticIdRef.current--;
            const now = new Date().toISOString();
            const optimisticComment = {
                id: tempId,
                contentJson,
                authorId: currentUser.id,
                createdAt: now,
                updatedAt: now,
                deletedAt: null,
                isEdited: false,
                isPostedAnonymously: false,
                threadId,
                parentCommentId: null,
                childCommentCount: 0,
                userCanEdit: true,
                author: {
                    type: "identified" as const,
                    id: currentUser.id,
                    name: currentUser.name,
                    avatarUrl: currentUser.avatarUrl ?? null,
                    initials: currentUser.initials,
                    isSocialEnabled: currentUser.isSocialEnabled,
                },
                // Attachments aren't included optimistically — the temp comment
                // shows the text immediately; attachments appear once the
                // server confirms and we swap in the real comment.
                attachments: [] as never[],
            };
            utils.discussions.getDiscussionView.setData(
                { discussionId: numericDiscussionId },
                (prev) =>
                    prev
                        ? {
                              ...prev,
                              comments: [
                                  ...prev.comments,
                                  optimisticComment as (typeof prev.comments)[number],
                              ],
                          }
                        : prev,
            );
            return { tempId };
        },
        onError: (_err, _input, ctx) => {
            if (!ctx?.tempId) return;
            utils.discussions.getDiscussionView.setData(
                { discussionId: numericDiscussionId },
                (prev) =>
                    prev
                        ? {
                              ...prev,
                              comments: prev.comments.filter((c) => c.id !== ctx.tempId),
                          }
                        : prev,
            );
        },
        onSuccess: (created, _input, ctx) => {
            utils.discussions.getDiscussionView.setData(
                { discussionId: numericDiscussionId },
                (prev) => {
                    if (!prev) return prev;
                    const withoutTemp = ctx?.tempId
                        ? prev.comments.filter((c) => c.id !== ctx.tempId)
                        : prev.comments;
                    return { ...prev, comments: [...withoutTemp, created] };
                },
            );

            // Patch every cached discussions.list query so inbox previews
            // pick up the new lastComment without a refetch. The list is
            // keyed by varying inputs (search, page, organizationId, etc.)
            // so we update all of them.
            const authorForListPreview =
                created.author.type === "identified"
                    ? {
                          type: "identified" as const,
                          name: created.author.name,
                          avatarUrl: created.author.avatarUrl ?? null,
                          initials: created.author.initials,
                      }
                    : created.author;
            const newLastComment = {
                authorId: created.authorId,
                author: authorForListPreview,
                createdAt: created.createdAt,
                isRead: true,
                isAuthorPrivateEnabled:
                    created.author.type === "identified"
                        ? (currentUser?.isPrivateEnabled ?? false)
                        : undefined,
            };
            rawQueryClient.setQueriesData(
                { queryKey: [["discussions", "list"]] },
                (old: unknown) => {
                    if (!old || typeof old !== "object") return old;
                    const data = old as {
                        discussions: Array<{ id: number; lastComment?: unknown }>;
                        totalFound: number;
                        total: number;
                    };
                    if (!Array.isArray(data.discussions)) return old;
                    return {
                        ...data,
                        discussions: data.discussions.map((d) =>
                            d.id === numericDiscussionId
                                ? { ...d, lastComment: newLastComment }
                                : d,
                        ),
                    };
                },
            );
        },
    });

    useEffect(() => {
        if (isError || comment === null) {
            navigate({ to: "/discussions" });
        }
    }, [isError, comment, navigate]);

    if (isLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (!comment) return null;

    return (
        <div className="mx-auto my-6 flex min-h-[86vh] w-full flex-col rounded-2xl border border-[#c8c8c8] bg-white p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-4">
                <Link
                    to="/discussions"
                    search={{ id: Number(discussionId) }}
                    className="cursor-pointer text-2xl font-bold text-black no-underline hover:underline"
                >
                    From{" "}
                    <span className="text-blue-500 hover:text-blue-600">
                        Discussion: {discussion?.title ?? `#${discussionId}`}
                    </span>
                </Link>
            </div>

            <div className="bg-brand-polar my-4 flex w-full flex-col rounded-2xl border border-[#c8c8c8] p-6 pt-4">
                <div className="flex w-full flex-row justify-between">
                    <div className="flex flex-row gap-4 ">
                        <Avatar
                            className="h-10 w-10 bg-gray-200"
                            photoUrl={
                                comment.author.type === "anonymous" ||
                                comment.author.type === "anonymous-self"
                                    ? anonymousAvatarSvg
                                    : comment.author.type === "compass-team"
                                      ? compassTeamAvatarSvg
                                      : comment.author.type === "deleted"
                                        ? deletedAvatarSvg
                                        : comment.author.avatarUrl
                            }
                            initials={
                                comment.author.type === "identified"
                                    ? comment.author.initials
                                    : undefined
                            }
                            name={comment.author.name}
                            seed={comment.authorId}
                        />
                        <div className="flex  flex-col ">
                            <div className="mb-5 flex w-full flex-col items-start text-sm font-semibold text-gray-800">
                                <div className="text-base text-black">
                                    {comment.author.type === "anonymous" &&
                                    "realName" in comment.author &&
                                    comment.author.realName ? (
                                        <Tooltip content={comment.author.realName as string}>
                                            <span className="cursor-help underline decoration-gray-400 decoration-dashed underline-offset-2">
                                                {comment.author.name}
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        comment.author.name
                                    )}
                                </div>
                                <div className="font-normal text-[#424242]">
                                    <div className="mt-2 flex min-w-max flex-row items-center justify-between gap-3 text-[#424242]">
                                        <div
                                            title={formatFullTimestamp(comment.createdAt, timeZone)}
                                        >
                                            {formatRelativeTime(comment.createdAt, timeZone)}
                                        </div>
                                        <div>
                                            <CopyShareButton
                                                textToCopy={window.location.href}
                                                className="min-w-max  border-none shadow-none"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex  w-full justify-end"></div>
                </div>

                <ProseDocument doc={comment.contentJson} />

                {comment.attachments.length > 0 && (
                    <div className="mt-3 flex flex-col gap-2">
                        {comment.attachments.map((attachment: ClientDocument) => (
                            <FileAttachment file={attachment} key={attachment.id} />
                        ))}
                    </div>
                )}
            </div>

            {thread && (
                <div className="mb-4 rounded border border-[#BDBDBD] bg-white p-6 shadow-sm">
                    <h3 className="mb-4 text-lg font-semibold text-[#424242]">Discussion thread</h3>
                    {threadComments.length > 0 ? (
                        <DiscussionComments
                            comments={threadComments}
                            discussionId={numericDiscussionId}
                            threadId={thread.id}
                        />
                    ) : (
                        <p className="mb-4 text-sm text-gray-500">
                            No messages yet in this thread.
                        </p>
                    )}

                    <div className="mt-4 border-t border-[#E5E7EB] pt-4">
                        <ThreadCommentForm
                            context="discussion"
                            onSubmit={({ contentJson, attachmentIds }) => {
                                replyMutation.mutate({
                                    threadId: thread.id,
                                    contentJson,
                                    attachmentIds,
                                });
                            }}
                            isLoading={replyMutation.isPending}
                            submitLabel="Send Message"
                            placeholder="Reply to this discussion"
                            templateOrganizationName={
                                discussion?.organizations?.find(
                                    (o) => o.name.trim().toLowerCase() !== "compass",
                                )?.name
                            }
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
